/**
 * Mobile (below `lg`) view of the request-a-quote modal: a full-screen slide-in panel
 * (`SliderMenu`) with a back/close bar, the quote-name input, one card per shown line, the
 * subtotal and the action buttons. Holds no request logic: every value and handler comes from
 * `QuoteModal.tsx` through `QuoteHelper.ts` → `QuoteModalType`. While open it locks page
 * scrolling (`overflow: hidden` on `body`, applied after 300 ms) and restores the previous body
 * style on close.
 *
 * Rule: "the customer is never shown a price the Prices or Quotes API didn't return"; "the APIs
 * may return a $0 value for price in which case we always show "TBD" to the end user and never
 * "$0"". Line prices go through `QuoteHelper.ts` → `priceOrTbd`; the subtotal shows TBD whenever
 * a line has a 0 price or is inactive. While prices load, the loading rule quoted in
 * `QuoteHelper.ts`'s header applies.
 *
 * Rule: "an order or payment request is never sent twice for one click", and the rule covers
 * quote and price requests too.
 *
 * Counterpart of `QuoteModalDesktop.tsx` (whose `PriceRequestResultButtons` it reuses). The two
 * differ today:
 * - Request Price calls `handleSaveAsList` directly. It is not disabled after a click, so each
 *   click sends another price request, and the Quotes API stores each as a new quote. This is a
 *   known defect against the single-send rule above.
 * - Remove & Reprice is disabled only when every line is 0-priced or inactive; desktop also
 *   disables it after a price request.
 * - The text inputs carry no `validation` prop.
 * - The "cannot be priced" warning shows when a line is inactive or its extended price is 0;
 *   desktop shows it for an inactive line or a 0 unit price.
 * - The subtotal shows an em dash when a line's `pricePerUom` is not a number; desktop tests
 *   `pricePerPound`. Against the loading rule this has the defects listed in the subtotal
 *   comment of `QuoteModalDesktop.tsx` → `ButtonArea`: an em dash after a failed pricing call
 *   or a returned line with no price, and TBD before any response in a reopened modal.
 */
import React, { createRef, RefObject, useEffect, useState } from "react";
import { SliderMenu } from "@components/Helper/commonStyles";
import { useTheme } from "@ryerson/frontend.theme";
import styled from "@emotion/styled";
import { Flex, FlexItem } from "@ryerson/frontend.layout";
import { Icon } from "@ryerson/frontend.assets";
import { Typography } from "@ryerson/frontend.typography";
import { Link } from "@ryerson/frontend.navigation";
import {
	CurrencyCode,
	displayPrice,
	getDisplayUom,
	MatchingProduct,
	navigateToProductDetails,
	useWindowSize,
	ShoppingCartPricingItem,
	determineProductDisplay,
	validateErpRestrictedCharacters,
	LanguageToLocale,
} from "@ryerson/frontend.common";
import rgba from "polished/lib/color/rgba";
import { css } from "@emotion/react";
import { Checkbox, ErpInput, Input } from "@ryerson/frontend.form-controls";
import { useApplication } from "@ryerson/frontend.application";
import { addImageDefaultSrc, getProductFormShapeImagePath } from "@components/Helper/common";
import { Divider } from "@components/CustomerDashboard/Helpers/StyledComponents";
import { Button } from "@ryerson/frontend.button";
import { Product } from "@ryerson/frontend.common";
import { emDash } from "@ryerson/frontend.common";
import { Alert } from "@ryerson/frontend.notification";
import {
	checkIsActiveItem,
	getPricingInformationForItem,
	HasActiveAvailableProps,
	intoErpValidator,
	priceOrTbd,
	QuoteInput,
	QuoteModalProductType,
	QuoteModalProductTypeKeys,
	QuoteModalType,
	resolveAttributeDisplay,
	UpdatedProduct,
} from "./QuoteHelper";
import DefineCutDisplay from "@components/Shared/DisplayDefineCut/DefineCutDisplay";
import { Popover } from "@ryerson/frontend.modal";
import { PriceRequestResultButtons } from "./QuoteModalDesktop";
import { withLanguage } from "./Content";
import {
	Localization,
	UpdatedProductAttribute,
} from "@components/Shared/ProductDisplay/Helpers/ProductDisplay.models";
import {
	ProductDetailMobile,
	ProductDetailSectionMobile,
	ProductDetailsMobileItem,
} from "./QuoteModal.Styled";

const GlobalNavigation = styled.div`
	height: 50px;
	width: 100%;
	top: 0;
	${(props: any) => {
		const { theme } = props;
		return css`
			border-bottom: 1px solid ${theme.colors.primary.lightGray};
			background-color: ${theme.colors.primary.darkGray};
		`;
	}}
`;

const closeFiltersPane = (props: any) => {
	const { white } = props;
	return css`
		height: 50px;
		width: 50px;
		border-left: 1px solid ${rgba(white, 0.1)};
	`;
};

const ModalContent = styled(Flex)`
	width: 100vw;
	overflow: auto;
	bottom: 0;
`;

const ModalContentInner = styled.div`
	padding: 20px;
`;

/** The mobile modal (see the file header). Rendered by `QuoteModal.tsx` below `lg`. */
const QuoteModalMobile: React.FC<QuoteModalType> = ({
	quoteName,
	setQuoteName,
	selectedProducts,
	showQuoteModal,
	setShowQuoteModal,
	content,
	quoteProductList,
	handleOnClickSaveQuote,
	pricingData,
	handleSaveAsList,
	handleRemoveReprice,
	quoteInputs,
	submitted,
	quoteCreateSubmitted,
	validationInputRef,
	priceRequestResult,
	setPriceRequestResult,
	isRetrievingPricing,
}) => {
	const { theme } = useTheme();
	const { gray, white, label, selected, background, lightGray, error } = theme.colors.primary;
	const windowSize = useWindowSize();
	const {
		localization: { language },
		isNative,
	} = useApplication();
	const local = Localization[language];
	const quoteModalContent = withLanguage[language];

	const [domNodeRefs] = useState<any>({});
	const [bodyStyles, setBodyStyles] = useState<string>("");
	const [hasMounted, setHasMounted] = React.useState<boolean>(false);
	const [containsUncertainPricesOrInactiveItems, setContainsUncertainPricesOrInactiveItems] =
		React.useState(false);
	const [allPricesUncertain, setAllPricesUncertain] = React.useState(false);
	const [showAlert, setShowAlert] = React.useState(false);

	const createDomNodeRef = (fieldName: string): RefObject<any> => {
		domNodeRefs[fieldName] = createRef();
		return domNodeRefs[fieldName];
	};

	// Decides the footer mode once pricing has returned, as `QuoteModalDesktop.tsx` does, and
	// whether Remove & Reprice would leave any line. Never reset between openings.
	React.useEffect(() => {
		if (
			hasMounted &&
			showQuoteModal &&
			pricingData?.items &&
			selectedProducts.length &&
			!isRetrievingPricing
		) {
			const hasInactiveUnavailableItems = (
				selectedProducts as HasActiveAvailableProps[]
			).some((item) => !checkIsActiveItem(item));
			let hasUncertainPrices = (pricingData?.items || []).some(
				(item: ShoppingCartPricingItem) => item.pricePerUom === 0
			);

			const pricingItemToSelectedProduct = function (item: ShoppingCartPricingItem) {
				return selectedProducts.find((product) => {
					return item.materialSku === product.materialMaster;
				});
			};

			const allItemsUncertainOrInactive =
				!!pricingData?.items?.length &&
				pricingData?.items.every(
					(item) =>
						item.pricePerUom === 0 ||
						!checkIsActiveItem(
							pricingItemToSelectedProduct(item) as HasActiveAvailableProps
						)
				);

			setAllPricesUncertain(allItemsUncertainOrInactive);

			setContainsUncertainPricesOrInactiveItems(
				() => hasUncertainPrices || hasInactiveUnavailableItems
			);
		}
	}, [hasMounted, pricingData, selectedProducts, showQuoteModal]);

	// Slides the panel in or out. The classes are toggled, not set, so each change of
	// `showQuoteModal` must be an open/close pair starting from the initial `slide-close`.
	useEffect(() => {
		if (hasMounted) {
			const sliderEl = domNodeRefs["save-as-quote-modal"].current;

			sliderEl?.classList?.toggle("slide-open");
			sliderEl?.classList?.toggle("slide-close");

			const body = document?.querySelector("body");
			if (showQuoteModal) {
				(window as any).scrollTo(0, 0);
				setBodyStyles(body?.getAttribute("style") || "");
				setTimeout(() => {
					body?.setAttribute("style", "overflow: hidden;");
				}, 300);
			} else {
				body && body.setAttribute("style", bodyStyles);
			}
		}
	}, [showQuoteModal]);

	// On mount, marks the component mounted and sets the parent's `showQuoteModal` to false, so
	// the modal starts closed, matching the panel's initial `slide-close` class.
	useEffect(() => {
		setHasMounted(true);
		setShowQuoteModal(false);
	}, []);

	React.useEffect(() => {
		if (!showQuoteModal) {
			setShowAlert(false);
		}
	}, [showQuoteModal]);

	const handleOnSaveQuote = () => {
		setShowAlert(false);
		handleOnClickSaveQuote();
		if (!quoteName) {
			window.document.getElementById("quote-top")?.scrollIntoView({ behavior: "smooth" });
			setShowAlert(true);
		}
	};

	const renderDescription = (
		product: UpdatedProduct,
		showDefineCut: boolean,
		index: number
	): JSX.Element => {
		return (
			<Flex
				css={{
					padding: "20px 20px 30px",
					background: selected,
					gap: 12,
				}}
			>
				<Flex direction="column" justifyContent="flex-start" css={{ color: background }}>
					<Flex
						css={{
							marginBottom: 7,
						}}
					>
						<Typography color={white}>{index + 1 + ". "}</Typography>
						<Typography underline={true} size="lg" color={background}>
							{product?.active ? (
								<Link
									type="secondary"
									to={navigateToProductDetails(
										product?.mageniumId,
										product?.altPartNumber || ""
									)}
									color={white}
									underline={true}
								>
									{determineProductDisplay(
										product?.altPartNumber || "",
										product?.fastPath
									)}
								</Link>
							) : (
								<Typography color={white}>
									{determineProductDisplay(
										product?.altPartNumber || "",
										product?.fastPath
									)}
								</Typography>
							)}
						</Typography>
					</Flex>
					{showDefineCut ? (
						<Typography weight="bold" color={white} size="md">
							{content.cutApplied}
						</Typography>
					) : (
						<Typography size="md" color={background}>
							{product?.itemDescriptionLocalization?.[locale]?.shortDescription}
						</Typography>
					)}
				</Flex>
			</Flex>
		);
	};

	const renderNavigation = (): JSX.Element => {
		return (
			<GlobalNavigation theme={theme}>
				<Flex alignItems="center">
					<FlexItem grow={1}>
						<Flex justifyContent="flex-start" onClick={() => setShowQuoteModal(false)}>
							<FlexItem alignSelf="center" css={{ marginLeft: 20 }}>
								<Icon
									icon="chevron-left"
									size="md"
									color={white}
									circled={true}
									css={{ borderColor: gray }}
								/>
							</FlexItem>
							<FlexItem
								css={css`
									padding-left: 10px;
								`}
							>
								<Typography
									variant="p"
									color={theme.colors.tertiary.label}
									size="md"
								>
									{content.back}
								</Typography>
							</FlexItem>
						</Flex>
					</FlexItem>
					<FlexItem>
						<Flex
							style={closeFiltersPane({ white })}
							justifyContent="center"
							alignItems="center"
						>
							<Icon
								icon="close"
								size="sm"
								onClick={() => setShowQuoteModal(false)}
								color={white}
							/>
						</Flex>
					</FlexItem>
				</Flex>
			</GlobalNavigation>
		);
	};

	const renderCutFrom = (product: UpdatedProduct): JSX.Element => {
		const { lighterGray } = theme.colors.primary;
		return (
			<>
				<Flex direction="row" css={{ marginBottom: 10, gap: 10 }}>
					<Typography weight="bold">{content.cutFrom}:</Typography>
					<Typography>
						{product?.itemDescriptionLocalization?.[locale]?.shortDescription}
					</Typography>
				</Flex>
				<Flex direction="column" css={{ marginBottom: 20 }}>
					<ProductDetailSectionMobile {...{ bgColor: `${background}` }}>
						<Flex direction="column">
							{product.attributes!.map((attribute, index) => {
								const { name, value } = resolveAttributeDisplay(
									attribute as UpdatedProductAttribute,
									language,
									local
								);
								return (
									<ProductDetailsMobileItem
										key={index}
										{...{ borderColor: `${lighterGray}` }}
									>
										<ProductDetailMobile justifyContent="space-between">
											<Typography weight="bold">{name}</Typography>
											<Typography>{value}</Typography>
										</ProductDetailMobile>
									</ProductDetailsMobileItem>
								);
							})}
						</Flex>
					</ProductDetailSectionMobile>
				</Flex>
			</>
		);
	};

	const renderProcessing = (
		product: Product | MatchingProduct,
		currentProductForQuote: QuoteModalProductType
	): JSX.Element => {
		return (
			<FlexItem
				css={{
					width: "100%",
					paddingBottom: "20px",
				}}
			>
				<DefineCutDisplay
					defineCutParameters={currentProductForQuote?.processing || {}}
					product={product}
					canEditCut={false}
					canDeleteCut={false}
				/>
			</FlexItem>
		);
	};

	/**
	 * One per-line input: the "domestic requested" checkbox with its help popover, or a text
	 * input. `attachValidationRef` puts `validationInputRef` on the input's wrapper; the caller
	 * computes it with `quoteProductList.findIndex(intoErpValidator)`, which is always 0, so the
	 * first card's inputs get it.
	 */
	const renderInputFields = (
		index: number,
		input: QuoteInput,
		currentProductForQuote: QuoteModalProductType,
		attachValidationRef?: boolean,
		validationInputRef?: React.MutableRefObject<HTMLElement | undefined>
	): JSX.Element => {
		const { white, label, lightGray } = theme.colors.primary;

		return !!input.type &&
			input.type === "checkbox" &&
			input.productTypeKey === QuoteModalProductTypeKeys.domesticRequired ? (
			<Flex
				key={index}
				direction="row"
				justifyContent="flex-start"
				css={{
					gap: 10,
					marginBottom: 10,
					width: "100%",
					alignItems: "center",
				}}
			>
				<Flex css={{ width: 281 }}>
					<Checkbox
						value={input.label}
						checked={currentProductForQuote[input.productTypeKey]}
						onChange={(e, checked) => {
							input.onChange(checked, currentProductForQuote);
						}}
						language={language}
					/>
				</Flex>
				<Popover
					offset={[-325, 10]}
					caretColor={theme.colors.primary.white}
					alignment={"bottom"}
					className={`trigger info-icon-${index}`}
					trigger={
						<span>
							<Icon
								icon="question-mark"
								size="sm"
								circled={true}
								css={{ backgroundColor: white }}
							/>
						</span>
					}
					dataTestId="domestic-required-help-popover"
				>
					<div
						css={{
							border: `1px solid ${rgba(lightGray, 0.6)}`,
							padding: 20,
							maxWidth: 300,
						}}
					>
						<Typography autoResize={false} size="sm" color={lightGray}>
							{content.domesticToolTip}
						</Typography>
					</div>
				</Popover>
			</Flex>
		) : (
			<Flex
				key={index}
				direction="column"
				justifyContent="space-between"
				css={{
					marginBottom: 10,
					width: "100%",
				}}
				itemRef={attachValidationRef ? validationInputRef : undefined}
			>
				<Typography color={label} size="md">
					{input.label}
				</Typography>
				<ErpInput
					onChange={(event) => input.onChange(event, currentProductForQuote)}
					value={currentProductForQuote[input.productTypeKey] as string}
					language={language}
					size="sm"
					css={{
						width: "100%",
					}}
				/>
			</Flex>
		);
	};

	const renderQuantity = (
		product: Product | MatchingProduct,
		itemPricingData: ShoppingCartPricingItem
	): JSX.Element => {
		return (
			<Flex
				justifyContent="space-between"
				css={{
					marginBottom: 5,
				}}
			>
				<Typography color={label} size="md">
					{content.quantity}
				</Typography>
				<FlexItem>
					<Typography size="md">
						{itemPricingData?.quantityUom &&
							`${itemPricingData.quantity} ${getDisplayUom(
								product?.form,
								itemPricingData?.quantityUom,
								"quantity"
							)}`}
					</Typography>
				</FlexItem>
			</Flex>
		);
	};

	const renderPrice = (
		pricePerUom: number,
		product: Product | MatchingProduct,
		itemPricingData: ShoppingCartPricingItem,
		inactiveItem: boolean
	): JSX.Element => {
		return (
			<Flex justifyContent="space-between" css={{ marginBottom: 5 }}>
				<Typography color={label} size="md">
					{`${pricingData.currency ? pricingData.currency : CurrencyCode.us} ${
						content.price
					}`}
				</Typography>
				<FlexItem>
					<Typography size="md" weight="bold" color={label}>
						{inactiveItem ? content.tbd : priceOrTbd(Number(pricePerUom), content.tbd)}
					</Typography>
					{itemPricingData?.pricingUom && (
						<Typography size="md" color={label}>
							{` / ${getDisplayUom(
								product?.form,
								itemPricingData?.pricingUom,
								"price"
							)}`}
						</Typography>
					)}
				</FlexItem>
			</Flex>
		);
	};

	/** True for an inactive or unavailable line while pricing is not in flight; it shows TBD. */
	const hasInactiveOrUnavailableItems = (currentProduct: HasActiveAvailableProps): boolean => {
		return !isRetrievingPricing && !checkIsActiveItem(currentProduct);
	};
	const locale = LanguageToLocale[language];
	return (
		<SliderMenu
			{...{ theme: theme }}
			ref={createDomNodeRef("save-as-quote-modal")}
			className="save-as-quote-modal slide-close"
			css={{ top: isNative ? "calc(env(safe-area-inset-top) + 60px)" : 100 }}
		>
			<Flex css={{ height: "100%" }} direction="column">
				<Flex css={{ height: "100%", overflowY: "scroll" }} direction="column">
					{renderNavigation()}
					<ModalContent
						direction="column"
						css={{ height: windowSize[1] - 100, overflowY: "scroll" }}
					>
						{priceRequestResult.display && priceRequestResult.success ? (
							<>
								<ModalContentInner>
									<Flex css={{ gap: 20 }} direction="column">
										<Typography size="lg" weight="bold">
											{content.successPriceRequest}
										</Typography>
										<Typography size="md">{content.reviewOrReturn}</Typography>
										<PriceRequestResultButtons
											priceRequestResult={priceRequestResult}
											setPriceRequestResult={setPriceRequestResult}
											content={content}
											isMobile={true}
											setShowQuoteModal={setShowQuoteModal}
										/>
									</Flex>
								</ModalContentInner>
							</>
						) : (
							<ModalContentInner>
								<span id="quote-top" />
								<Typography variant="h2">
									{!(priceRequestResult.display && priceRequestResult.success)
										? content.saveAsQuote
										: content.priceRequest}
								</Typography>
								{showAlert && (
									<div css={{ margin: "24px 0px" }}>
										<Alert severity="error" size="sm">
											<Typography>
												{quoteModalContent.errorMessage}
											</Typography>
										</Alert>
									</div>
								)}
								{!containsUncertainPricesOrInactiveItems && !isRetrievingPricing ? (
									<Flex css={{ margin: "24px 0px 32px" }} direction="column">
										<FlexItem>
											<Typography
												color={label}
												size="lg"
												css={{ marginBottom: 5 }}
											>
												{content.quoteName}
											</Typography>
											<Typography color={error} variant="span">
												*
											</Typography>
										</FlexItem>
										<Input
											label=""
											size="sm"
											maxLength={15}
											required={true}
											onChange={(event) => setQuoteName(event.target.value)}
											value={quoteName}
											type="secondary"
											language={language}
											isValid={
												submitted
													? Boolean(
															quoteName &&
															!validateErpRestrictedCharacters(
																quoteName
															)
														)
													: true
											}
											submitted={submitted}
											validation={validateErpRestrictedCharacters}
										/>
									</Flex>
								) : (
									<></>
								)}
								<Flex
									direction="column"
									css={{
										marginTop: 22,
									}}
								>
									<Flex
										direction="column"
										css={{
											margin: "28px 0px 27px",
											gap: 20,
										}}
									>
										{quoteProductList?.map(
											(currentProductForQuote, productIndex: number) => {
												const showDefineCut =
													Object.keys(
														currentProductForQuote?.processing || {}
													).length > 0;
												/**
												 * We can’t identify items using indices here.
												 * That’s because {@link selectedProducts} isn’t a fixed-sized list.
												 * Its indices and items change when the user hits `remove & reprice`.
												 * Let’s use ids instead.
												 */
												let currentProduct = selectedProducts.find(
													(product) =>
														currentProductForQuote.materialSku ===
															product.materialMaster &&
														currentProductForQuote.id === product.id
												);
												if (currentProduct) {
													const {
														pricePerUom,
														extendedPrice,
														itemPricingData,
													} = getPricingInformationForItem(
														productIndex,
														currentProductForQuote,
														pricingData,
														currentProduct
													);
													return (
														<FlexItem
															css={{
																marginBottom:
																	productIndex ===
																	selectedProducts?.length - 1
																		? 20
																		: 0,
															}}
															key={productIndex}
														>
															{renderDescription(
																currentProduct as UpdatedProduct,
																showDefineCut,
																productIndex
															)}

															<FlexItem css={{ margin: "10px 0" }}>
																<img
																	onError={(event) =>
																		addImageDefaultSrc(event)
																	}
																	src={getProductFormShapeImagePath(
																		currentProduct?.category ||
																			"",
																		currentProduct?.form || "",
																		currentProduct?.shape || ""
																	)}
																	{...{
																		backgroundColor: white,
																		height: "90px",
																	}}
																	css={{
																		height: "90px",
																		border: `1px solid ${lightGray}`,
																		borderRadius: "3px",
																		padding: "10px 0",
																	}}
																/>
															</FlexItem>
															<Flex
																direction="column"
																css={{
																	padding: "17px 20px 24px",
																	background: background,
																}}
															>
																{renderQuantity(
																	currentProduct,
																	itemPricingData
																)}
																<Divider
																	theme={theme}
																	css={{
																		marginBottom: 9,
																	}}
																/>
																{renderPrice(
																	pricePerUom,
																	currentProduct,
																	itemPricingData,
																	hasInactiveOrUnavailableItems(
																		currentProduct as HasActiveAvailableProps
																	)
																)}

																<Divider
																	theme={theme}
																	css={{
																		marginBottom: 9,
																	}}
																/>
																<Flex
																	alignItems="center"
																	justifyContent="space-between"
																	css={{
																		marginBottom: 21,
																	}}
																>
																	<Typography
																		color={label}
																		size="md"
																	>
																		{`${
																			pricingData.currency
																				? pricingData.currency
																				: CurrencyCode.us
																		} ${content.extendedPrice}`}
																	</Typography>
																	<Typography
																		size="md"
																		weight="bold"
																		color={label}
																	>
																		{hasInactiveOrUnavailableItems(
																			currentProduct as HasActiveAvailableProps
																		)
																			? content.tbd
																			: priceOrTbd(
																					Number(
																						extendedPrice
																					),
																					content.tbd
																				)}
																	</Typography>
																</Flex>
																{Number(extendedPrice) === 0 ||
																hasInactiveOrUnavailableItems(
																	currentProduct as HasActiveAvailableProps
																) ? (
																	<Flex
																		css={{
																			marginBottom: "25px",
																		}}
																	>
																		<Icon
																			icon="warning"
																			size="sm"
																			css={{
																				marginRight: "15px",
																			}}
																		/>
																		<Typography>
																			{
																				content.itemCannotBePriced
																			}
																		</Typography>
																	</Flex>
																) : (
																	""
																)}
																{showDefineCut &&
																	!!(
																		currentProduct as UpdatedProduct
																	)?.attributes?.length &&
																	renderCutFrom(
																		currentProduct as UpdatedProduct
																	)}
																{showDefineCut &&
																	renderProcessing(
																		currentProduct,
																		currentProductForQuote
																	)}
																{quoteInputs?.map(
																	(input, index) => {
																		return renderInputFields(
																			index,
																			input,
																			currentProductForQuote,
																			quoteProductList.findIndex(
																				intoErpValidator
																			) === productIndex,
																			validationInputRef
																		);
																	}
																)}
															</Flex>
														</FlexItem>
													);
												} else {
													return <></>;
												}
											}
										)}
									</Flex>
									<Divider theme={theme} />
									<Flex
										justifyContent="flex-end"
										css={{ margin: "20px 0px 32px" }}
									>
										<Flex direction="column">
											<Flex justifyContent="flex-end">
												<Typography color={label} size="md">
													{content.subtotal}
												</Typography>
											</Flex>
											<Flex alignItems="center">
												<Typography
													color={label}
													variant="p"
													weight="bold"
													css={{ paddingTop: "6px" }}
												>
													{!containsUncertainPricesOrInactiveItems &&
														pricingData.currency}
													&nbsp;
												</Typography>
												<Typography
													color={label}
													variant="h3"
													weight="bold"
												>
													{
														{
															true: `${
																displayPrice(
																	Number(pricingData.subtotal)
																).split(".")[0]
															}`,
															[(
																!pricingData?.items?.length ||
																pricingData.items.some((item) =>
																	Number.isNaN(
																		Number(item.pricePerUom)
																	)
																)
															).toString()]: emDash,
															[containsUncertainPricesOrInactiveItems?.toString()]:
																content.tbd,
														}["true"]
													}
												</Typography>
												<Typography
													color={label}
													variant="p"
													weight="bold"
													css={{ paddingTop: "6px" }}
												>
													{
														{
															true: `.${
																Number(pricingData.subtotal)
																	.toFixed(2)
																	.split(".")[1]
															}`,
															[(
																!pricingData?.items?.length ||
																pricingData.items.some((item) =>
																	Number.isNaN(
																		Number(item.pricePerUom)
																	)
																)
															).toString()]: "",
															[containsUncertainPricesOrInactiveItems?.toString()]:
																"",
														}["true"]
													}
												</Typography>
											</Flex>
										</Flex>
									</Flex>
									{priceRequestResult.display && !priceRequestResult.success ? (
										<Flex
											alignItems="center"
											css={{
												width: "100%",
												margin: "0px 0px 20px 0px;",
											}}
										>
											<FlexItem>
												<Icon icon="error" size="md" />
											</FlexItem>
											<FlexItem>
												<Typography
													color={label}
													css={{
														marginLeft: "12px",
														display: "block",
													}}
												>
													{content.failPriceRequest}
													<Link
														next={false}
														to={`mailTo:${content.brandedEmail}`}
													>
														{content.brandedEmail}
													</Link>
												</Typography>
											</FlexItem>
										</Flex>
									) : (
										<></>
									)}
									{!priceRequestResult.display &&
									!priceRequestResult.success &&
									containsUncertainPricesOrInactiveItems ? (
										<>
											{!isRetrievingPricing && (
												<Flex>
													<FlexItem css={{ minWidth: "30px" }}>
														<Icon icon="warning" size="lg" />
													</FlexItem>
													<FlexItem>
														<Typography
															color={label}
															css={{
																margin: "0 0 20px 10px",
																display: "block",
															}}
														>
															{content.disclaimer}
															<br />
															<br />
															{content.disclaimer2}
														</Typography>
													</FlexItem>
												</Flex>
											)}
											<Flex direction="column" css={{ marginBottom: 20 }}>
												{!isRetrievingPricing ? (
													<>
														<Button
															label={content.requestPrice}
															onClick={handleSaveAsList}
															fullwidth={true}
															css={{
																width: "100%",
																marginBottom: 20,
															}}
															dataTestId="quote-add-to-shopping-list"
														/>
														<Button
															label={content.removeAndReprice}
															onClick={handleRemoveReprice}
															disabled={allPricesUncertain}
															fullwidth={true}
															css={{
																width: "100%",
																marginBottom: 20,
															}}
															type="secondary"
															dataTestId="quote-remove-and-reprice"
														/>
													</>
												) : (
													<Typography
														css={{ marginBottom: 20 }}
														size="md"
														color={label}
													>
														{content.processingRequest}
													</Typography>
												)}
											</Flex>
										</>
									) : (
										<>
											{!isRetrievingPricing ? (
												<>
													<Button
														label={content.saveQuote}
														disabled={quoteCreateSubmitted}
														onClick={() => handleOnSaveQuote()}
														fullwidth={true}
														css={{ width: "100%", marginBottom: 20 }}
														dataTestId="save-as-quote"
													/>

													<Flex css={{ marginBottom: 72 }}>
														<Typography size="sm" color={label}>
															{content.clickQuotes}
														</Typography>
													</Flex>
												</>
											) : (
												<Typography
													css={{ marginBottom: 72 }}
													size="md"
													color={label}
												>
													{content.processingRequest}
												</Typography>
											)}
										</>
									)}
								</Flex>
							</ModalContentInner>
						)}
					</ModalContent>
				</Flex>
			</Flex>
		</SliderMenu>
	);
};

export default QuoteModalMobile;
